import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ServiceType } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Field,
  FormGrid,
  Input,
  InlineRow,
  Muted,
  Select,
  Stack,
  Strong,
} from '../../components/ui';
import {
  useMyServiceJobsQuery,
  useServiceWorkerQuery,
  useSyncServiceSiteCalendarMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { CheckRow } from '../ServiceProviderPage/serviceControls';
import { formatMoney } from '../ServiceProviderPage/priceTables';
import { stayErrorMessage } from '../StayHostPage/stayErrors';
import { WINDOW_TIMES } from './serviceSearchState';
import type { ServiceSite, SiteForm, SiteFormErrors } from './serviceSiteForm';

/**
 * "Connect your Airbnb or Vrbo calendar" on a home (spec 019 W28, D19, T070):
 * the export link, the worker and service to book after each checkout, the
 * window and add-ons. The sync books a job for each new checkout; "Sync now"
 * reads the calendar at once. The handover limit does not apply to homes.
 */
export function CalendarSyncSection({
  site,
  form,
  errors,
  set,
}: {
  site?: ServiceSite | null;
  form: SiteForm;
  errors: SiteFormErrors;
  set: (patch: Partial<SiteForm>) => void;
}) {
  const [open, setOpen] = useState(Boolean(form.calendarUrl));
  const change = (patch: Partial<SiteForm>) => set({ ...patch, calendarChanged: true });
  const jobs = useMyServiceJobsQuery({ variables: { limit: 50 }, skip: !open });
  const workers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const job of jobs.data?.myServiceJobs ?? []) {
      const w = job.car?.serviceWorker;
      const items = Array.isArray(job.serviceJob?.items) ? (job.serviceJob!.items as any[]) : [];
      const isHome = items.some(
        i => String(i?.serviceType ?? '').toLowerCase() === 'home_turnover',
      );
      if (w && isHome) seen.set(w.carId, w.displayName);
    }
    return Array.from(seen.entries());
  }, [jobs.data]);
  const worker = useServiceWorkerQuery({
    variables: { carId: form.calendarWorkerCarId },
    skip: !form.calendarWorkerCarId,
  });
  const turnovers = (worker.data?.serviceWorker?.serviceWorker?.offerings ?? []).filter(
    o => o.isActive && o.serviceType === ServiceType.HomeTurnover,
  );
  const offering = turnovers.find(o => o.id === form.calendarOfferingId) ?? null;
  const [sync, { loading: syncing }] = useSyncServiceSiteCalendarMutation();

  if (!open) {
    return (
      <div>
        <Button
          type="button"
          theme="secondary"
          size="sm"
          text="Connect your Airbnb or Vrbo calendar"
          onClick={() => setOpen(true)}
          dataTestId="site-calendar-open"
        />
      </div>
    );
  }

  const workerOptions =
    form.calendarWorkerCarId && !workers.some(([id]) => id === form.calendarWorkerCarId)
      ? [
          ...workers,
          [
            form.calendarWorkerCarId,
            worker.data?.serviceWorker?.serviceWorker?.displayName ?? 'Your worker',
          ] as [string, string],
        ]
      : workers;

  return (
    <Stack $gap={4} data-testid="site-calendar">
      <Strong>Book a cleaning after each checkout</Strong>
      <Muted $size="sm">
        Paste your listing's calendar export link. On Airbnb: Calendar, Availability, Connect to
        another website, then copy the Airbnb calendar link. On Vrbo: Calendar, Import and export,
        Export calendar. We read it every few hours and book your worker on each checkout day; you
        can cancel any of those jobs.
      </Muted>
      <Field label="Calendar link" htmlFor="site-calendar-url" error={errors.calendarUrl}>
        <Input
          id="site-calendar-url"
          placeholder="https://www.airbnb.com/calendar/ical/….ics"
          value={form.calendarUrl}
          onChange={e => change({ calendarUrl: e.target.value })}
          data-testid="site-calendar-url"
        />
      </Field>
      {form.calendarUrl.trim() && (
        <>
          <FormGrid $cols={2}>
            <Field
              label="Worker"
              htmlFor="site-calendar-worker"
              error={errors.calendarWorkerCarId}
              hint={workerOptions.length ? undefined : 'Book a worker once, then pick them here.'}
            >
              <Select
                id="site-calendar-worker"
                value={form.calendarWorkerCarId}
                onChange={e =>
                  change({
                    calendarWorkerCarId: e.target.value,
                    calendarOfferingId: '',
                    calendarAddOns: {},
                  })
                }
              >
                <option value="">Pick a worker</option>
                {workerOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Service"
              htmlFor="site-calendar-offering"
              error={errors.calendarOfferingId}
            >
              <Select
                id="site-calendar-offering"
                value={form.calendarOfferingId}
                disabled={!turnovers.length}
                onChange={e => change({ calendarOfferingId: e.target.value, calendarAddOns: {} })}
              >
                <option value="">
                  {form.calendarWorkerCarId ? 'Pick the service' : 'Pick a worker first'}
                </option>
                {turnovers.map(o => (
                  <option key={o.id} value={o.id}>
                    Home turnover
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Window starts"
              htmlFor="site-calendar-from"
              error={errors.calendarWindowStart}
            >
              <Select
                id="site-calendar-from"
                value={form.calendarWindowStart}
                onChange={e => change({ calendarWindowStart: e.target.value })}
              >
                {WINDOW_TIMES.map(t => (
                  <option key={t} value={t}>
                    {DateTime.fromFormat(t, 'HH:mm').toFormat('h:mm a')}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Window ends"
              htmlFor="site-calendar-to"
              error={errors.calendarWindowEnd}
              hint="Same day. Guests usually check out by 11 AM and in from 3 PM."
            >
              <Select
                id="site-calendar-to"
                value={form.calendarWindowEnd}
                onChange={e => change({ calendarWindowEnd: e.target.value })}
              >
                {WINDOW_TIMES.map(t => (
                  <option key={t} value={t}>
                    {DateTime.fromFormat(t, 'HH:mm').toFormat('h:mm a')}
                  </option>
                ))}
              </Select>
            </Field>
          </FormGrid>
          {offering && offering.addOns.length > 0 && (
            <Stack $gap={4}>
              <Muted $size="sm">Add-ons for every job</Muted>
              {offering.addOns.map(a => (
                <CheckRow
                  key={a.code}
                  label={`${a.label} (${formatMoney(a.price)})`}
                  checked={(form.calendarAddOns[a.code] ?? 0) > 0}
                  onChange={on =>
                    change({ calendarAddOns: { ...form.calendarAddOns, [a.code]: on ? 1 : 0 } })
                  }
                />
              ))}
            </Stack>
          )}
        </>
      )}
      {site?.externalCalendarUrl && (
        <Stack $gap={4}>
          {site.calendarLastError ? (
            <Alert tone="warning" title="The calendar did not read">
              {site.calendarLastError}
            </Alert>
          ) : (
            <Muted $size="sm" data-testid="site-calendar-synced">
              {site.calendarLastSyncAt
                ? `Last read ${DateTime.fromISO(String(site.calendarLastSyncAt)).toFormat('LLL d, h:mm a')}.`
                : 'Not read yet.'}
            </Muted>
          )}
          <InlineRow>
            <Button
              type="button"
              theme="secondary"
              size="sm"
              text="Sync now"
              isLoading={syncing}
              isDisabled={form.calendarChanged}
              onClick={async () => {
                try {
                  const { data } = await sync({ variables: { siteId: site.id } });
                  const error = data?.syncServiceSiteCalendar.calendarLastError;
                  if (error) toast.error(error);
                  else toast.success('Calendar read');
                } catch (e) {
                  toast.error(stayErrorMessage(e, 'Could not read the calendar'));
                }
              }}
              dataTestId="site-calendar-sync"
            />
            {form.calendarChanged && <Muted $size="sm">Save first, then sync.</Muted>}
          </InlineRow>
        </Stack>
      )}
    </Stack>
  );
}
